/**
 * T138 — PrivateUseOverrideField.
 *
 * FR-005e: the Geschäftsführer overrides the auto-derived deductibility (from
 * category + accounting-class snapshot) with a per-expense percentage. When
 * the override is set, a reason ≥ 10 Zeichen is required.
 *
 * Preview shows:
 *   - resulting Absetzbarkeits-% = privateUseOverridePercent (the business
 *     part; the rest is Privatanteil and not deductible)
 *   - resulting Vorsteuer-Anteil % (mirrors the Absetzbarkeits-% since USt
 *     follows the same split)
 */
import { useId, useMemo, useState } from 'react';
import { formatNumber } from '@armard/shared';

export interface PrivateUseOverrideFieldProps {
  value: number | null;
  reason: string | null;
  onChange: (next: { percent: number | null; reason: string | null }) => void;
  disabled?: boolean;
  /** When no override is set, the Absetzbarkeits-% the category snapshots — shown as a hint */
  fallbackDeductiblePercent?: number | null;
}

export function PrivateUseOverrideField({
  value,
  reason,
  onChange,
  disabled = false,
  fallbackDeductiblePercent = null,
}: PrivateUseOverrideFieldProps) {
  const fieldId = useId();
  const [touched, setTouched] = useState(false);

  const overrideActive = value != null;
  const safePercent = value ?? fallbackDeductiblePercent ?? 100;
  const reasonTooShort = overrideActive && (reason?.trim().length ?? 0) < 10;
  const showReasonError =
    overrideActive && (touched || (reason?.length ?? 0) > 0) && reasonTooShort;

  const preview = useMemo(
    () => ({
      deductible: formatNumber(safePercent),
      privat: formatNumber(100 - safePercent),
    }),
    [safePercent]
  );

  function setPercent(next: number | null) {
    onChange({ percent: next, reason: next == null ? null : (reason ?? '') });
  }
  function setReason(next: string) {
    onChange({ percent: value, reason: next });
  }

  return (
    <fieldset
      disabled={disabled}
      className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3"
      data-testid="private-use-override-field"
    >
      <legend className="text-sm font-medium text-white/70 px-1">Absetzbarkeit</legend>

      <label className="flex items-center gap-2 text-white/80 select-none">
        <input
          type="checkbox"
          checked={overrideActive}
          onChange={(e) => setPercent(e.target.checked ? 100 : null)}
          className="rounded bg-white/5 border-white/10"
          data-testid="private-use-override-toggle"
        />
        Abweichende Absetzbarkeit angeben
      </label>

      {overrideActive && (
        <>
          <div>
            <label
              htmlFor={`${fieldId}-percent`}
              className="block text-sm font-medium text-white/70 mb-1.5"
            >
              Absetzbarkeit: {safePercent.toFixed(0)} %
            </label>
            <input
              id={`${fieldId}-percent`}
              type="range"
              min={0}
              max={100}
              step={1}
              value={safePercent}
              onChange={(e) => setPercent(Number(e.target.value))}
              className="w-full"
              data-testid="private-use-override-slider"
            />
            <div className="flex justify-between text-xs text-white/50 mt-0.5">
              <span>0 %</span>
              <span>50 %</span>
              <span>100 %</span>
            </div>
          </div>

          <div>
            <label
              htmlFor={`${fieldId}-reason`}
              className="block text-sm font-medium text-white/70 mb-1.5"
            >
              Begründung <span className="text-red-300">*</span>
            </label>
            <textarea
              id={`${fieldId}-reason`}
              aria-invalid={showReasonError}
              value={reason ?? ''}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              rows={2}
              placeholder="z. B. Homeoffice-Anteil 60 % nach Nutzungsnachweis"
              className={`w-full px-4 py-2.5 rounded-lg bg-white/5 border text-white ${
                showReasonError ? 'border-red-500/50' : 'border-white/10'
              }`}
              data-testid="private-use-override-reason"
            />
            {showReasonError && (
              <div
                className="text-xs text-red-300 mt-1"
                data-testid="private-use-override-reason-error"
              >
                Mindestens 10 Zeichen für eine nachvollziehbare Begründung.
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm text-white/80">
            <div className="rounded border border-white/10 bg-white/5 px-3 py-2">
              <div className="text-xs text-white/50">Absetzbar (Betrieb)</div>
              <div className="text-lg tabular-nums" data-testid="private-use-override-deductible">
                {preview.deductible} %
              </div>
            </div>
            <div className="rounded border border-white/10 bg-white/5 px-3 py-2">
              <div className="text-xs text-white/50">Privat-Anteil</div>
              <div className="text-lg tabular-nums" data-testid="private-use-override-private">
                {preview.privat} %
              </div>
            </div>
          </div>

          <div className="text-xs text-white/40">
            Die Vorsteuer folgt demselben Absetzbarkeits-% (analog §15 UStG) — der Steuerberater
            kann den Wert im Review ggf. überschreiben.
          </div>
        </>
      )}

      {!overrideActive && fallbackDeductiblePercent != null && (
        <div className="text-xs text-white/40">
          Es gilt die Absetzbarkeit der Kategorie ({fallbackDeductiblePercent.toFixed(0)} %).
        </div>
      )}

      <input
        type="hidden"
        name="private_use_override_percent"
        value={value == null ? '' : String(value)}
      />
      <input type="hidden" name="private_use_reason" value={reason ?? ''} />
    </fieldset>
  );
}
